/**
 * ref: https://react-spectrum.adobe.com/react-aria-tailwind-starter/?path=/docs/slider--docs
 * ref: https://ui.shadcn.com/docs/components/slider
 */
import {
  Slider as AriaSlider,
  type SliderProps as AriaSliderProps,
  SliderOutput,
  SliderThumb,
  SliderTrack,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { Label } from "./Label";
import { focusRing } from "../utils/focusRing";
import { composeTailwindRenderProps } from "../utils/utils";

const trackStyles = tv({
  base: "rounded-full",
  variants: {
    orientation: {
      horizontal: "h-1.5 w-full",
      vertical: "-translate-x-[50%] ml-[50%] h-full w-1.5",
    },
    isMultiple: {
      true: "bg-primary forced-colors:bg-[ButtonBorder]",
      false: "bg-primary/40 forced-colors:bg-[ButtonBorder]",
    },
    isDisabled: {
      true: "bg-muted forced-colors:bg-[GrayText]",
    },
    colorVariant: {
      default: "bg-primary/40",
      exposure: "track-exposure-gradient",
      brightness: "track-brightness-gradient",
      contrast: "track-contrast-gradient",
      temperature: "track-temperature-gradient",
      tint: "track-tint-gradient",
      saturation: "track-saturation-gradient",
      shadows: "track-shadows-gradient",
      highlights: "track-highlights-gradient",
      midtones: "track-midtones-gradient",
    },
  },
});

const thumbStyles = tv({
  extend: focusRing,
  base: "h-3 w-3 rounded-full border border-border bg-foreground shadow-sm group-orientation-horizontal:mt-3 group-orientation-vertical:ml-1.5",
  variants: {
    isDragging: {
      true: "hover:bg-purple-gradient forced-colors:bg-[ButtonBorder]",
    },
    isDisabled: {
      true: "border-muted forced-colors:border-[GrayText]",
    },
  },
});

export type SliderColorVariant =
  | "default"
  | "exposure"
  | "brightness"
  | "contrast"
  | "temperature"
  | "tint"
  | "saturation"
  | "shadows"
  | "highlights"
  | "midtones";

export interface SliderProps<T> extends AriaSliderProps<T> {
  label?: string;
  thumbLabels?: string[];
  colorVariant?: SliderColorVariant;
}

export function Slider<T extends number | number[]>({
  label,
  thumbLabels,
  colorVariant = "default",
  ...props
}: Readonly<SliderProps<T>>) {
  return (
    <AriaSlider
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "orientation-vertical:flex orientation-horizontal:grid w-full grid-cols-[1fr_auto] flex-col items-center gap-2"
      )}
    >
      <Label>{label}</Label>
      <SliderOutput className="orientation-vertical:hidden font-medium text-muted-foreground text-sm">
        {({ state }) => state.values.map((_, i) => state.getThumbValueLabel(i)).join(" - ")}
      </SliderOutput>
      <SliderTrack className="group col-span-2 flex orientation-horizontal:h-3 orientation-vertical:h-64 orientation-vertical:w-3 items-center">
        {({ state, ...renderProps }) => (
          <>
            <div
              className={trackStyles({
                ...renderProps,
                isMultiple: state.values.length > 1,
                colorVariant,
              })}
            />
            {state.values.map((_, i) => (
              <SliderThumb
                key={`_slider_thumb_${i.toString()}`}
                index={i}
                aria-label={thumbLabels?.[i]}
                className={thumbStyles(renderProps)}
              />
            ))}
          </>
        )}
      </SliderTrack>
    </AriaSlider>
  );
}
